import React, { useState, useRef } from "react";
import { Upload, Link2, Loader2, Landmark } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CnePrecios } from "@/components/control/CnePrecios";
import { CneComparar } from "@/components/control/CneComparar";
import { CnePlantas } from "@/components/control/CnePlantas";
import { CneNotificacion } from "@/components/control/CneNotificacion";
import { ExportExcelButton } from "@/components/ExportExcelButton";

function CneActions() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(null);
  const [url, setUrl] = useState("");
  const run = async (kind, fn) => {
    setBusy(kind);
    try {
      const { data } = await fn();
      toast.success(`${data.actualizada ? "Actualizada" : "Importada"}: semana ${data.week} de ${data.year} · ${data.registros} registros.`);
      qc.invalidateQueries({ queryKey: ["cne"] });
      setUrl("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo actualizar.");
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };
  const onFile = (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    run("file", () => api.post("/control-semanal/cne/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } }));
  };
  const btn = "h-10 px-4 rounded-xl text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50";
  return (
    <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 flex flex-wrap items-center gap-3" data-testid="cne-actions">
      <input ref={fileRef} type="file" accept=".pdf" className="hidden" data-testid="cne-file-input" onChange={(e) => onFile(e.target.files?.[0])} />
      <button onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="cne-import-file-btn" className={`${btn} border border-slate-200 text-slate-700 hover:bg-slate-50`}>
        {busy === "file" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Importar PDF oficial
      </button>
      <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0 sm:min-w-[280px]">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://www.gob.mx/cms/uploads/…/PRECIOS_MAXIMOS_VIGENTES….pdf" data-testid="cne-url-input"
          className="h-10 w-full min-w-0 sm:w-auto sm:flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
        <button onClick={() => run("url", () => api.post("/control-semanal/cne/import-url", { url }))} disabled={!!busy || !url.trim()} data-testid="cne-import-url-btn" className={`${btn} border border-slate-200 text-slate-700 hover:bg-slate-50`}>
          {busy === "url" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}Importar enlace
        </button>
      </div>
      <CneNotificacion />
    </div>
  );
}

export default function PreciosCNE() {
  const { user } = useAuth();
  const canEdit = user?.is_admin || (user?.caps || []).includes("cne_cap");
  return (
    <div className="w-full space-y-6" data-testid="precios-cne">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Control Semanal</p>
        <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">Precios de venta (CNE)</h1>
        <p className="text-sm text-slate-500 mt-1 flex items-center gap-1.5"><Landmark className="w-4 h-4" />Precios máximos oficiales de Gas LP publicados por la Comisión Nacional de Energía (vigencia de domingo a sábado).</p>
      </div>
      {canEdit && user?.is_admin && <CneActions />}
      <div className="flex justify-end">
        <ExportExcelButton testid="cne-export" fileName="Precios_CNE" titulo="Precios máximos de Gas LP (CNE)"
          hojas={[{ name: "Precios por región", node: <CnePrecios /> }, { name: "Comparar periodos", node: <CneComparar /> }, { name: "Plantas y regiones", node: <CnePlantas canEdit={false} /> }]} />
      </div>
      <Tabs defaultValue="precios">
        <TabsList data-testid="cne-tabs">
          <TabsTrigger value="precios" data-testid="cne-tab-precios">Precios por región</TabsTrigger>
          <TabsTrigger value="comparar" data-testid="cne-tab-comparar">Comparar periodos</TabsTrigger>
          <TabsTrigger value="plantas" data-testid="cne-tab-plantas">Plantas y regiones</TabsTrigger>
        </TabsList>
        <TabsContent value="precios" className="mt-4"><CnePrecios /></TabsContent>
        <TabsContent value="comparar" className="mt-4"><CneComparar /></TabsContent>
        <TabsContent value="plantas" className="mt-4"><CnePlantas canEdit={canEdit} /></TabsContent>
      </Tabs>
    </div>
  );
}
